import { useState, type ReactNode } from 'react'
import { PanelLeft, PanelRight, Square } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useStudioLocale } from '@/i18n'
import { COOGO_IDE_CHROME_COPY } from '../coogo-ide-chrome-copy'

type CompactPane = 'navigator' | 'workspace' | 'inspector'

interface StudioEditorHostProps {
  toolbar?: ReactNode
  navigator?: ReactNode
  navigatorLabel?: string
  navigatorWidth?: number
  inspector?: ReactNode
  inspectorLabel?: string
  inspectorWidth?: number
  children: ReactNode
  className?: string
  canvasClassName?: string
  testId?: string
}

/**
 * Shared frame for every Studio artifact editor. The artifact remains the
 * dominant region while navigation and detail controls keep stable bounds.
 */
export function StudioEditorHost({
  toolbar,
  navigator,
  navigatorLabel,
  navigatorWidth = 224,
  inspector,
  inspectorLabel,
  inspectorWidth = 320,
  children,
  className,
  canvasClassName,
  testId = 'studio-editor-host',
}: StudioEditorHostProps) {
  const { locale } = useStudioLocale()
  const copy = COOGO_IDE_CHROME_COPY[locale]
  const [compactPane, setCompactPane] = useState<CompactPane>('workspace')
  const compactOptions: Array<{
    id: CompactPane
    label: string
    icon: typeof Square
  }> = [
    ...(navigator ? [{ id: 'navigator' as const, label: copy.navigate, icon: PanelLeft }] : []),
    { id: 'workspace', label: copy.workspace, icon: Square },
    ...(inspector ? [{ id: 'inspector' as const, label: copy.details, icon: PanelRight }] : []),
  ]

  return (
    <div
      className={cn('flex h-full min-h-0 flex-col bg-background', className)}
      data-testid={testId}
      data-compact-pane={compactPane}
    >
      {toolbar}
      {compactOptions.length > 1 ? (
        <div
          className="grid shrink-0 grid-flow-col auto-cols-fr gap-1 border-b border-border bg-card/70 p-1 sm:hidden"
          role="group"
          aria-label={copy.workspacePane}
          data-testid="studio-editor-compact-switcher"
        >
          {compactOptions.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              aria-pressed={compactPane === id}
              onClick={() => setCompactPane(id)}
              className={cn(
                'inline-flex h-8 min-w-0 items-center justify-center gap-1.5 rounded-md px-2 text-xs font-medium transition-colors',
                compactPane === id
                  ? 'bg-background text-foreground shadow-sm ring-1 ring-border'
                  : 'text-muted-foreground hover:bg-background/70 hover:text-foreground',
              )}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="truncate">{label}</span>
            </button>
          ))}
        </div>
      ) : null}
      <div className="flex min-h-0 min-w-0 flex-1">
        {navigator ? (
          <aside
            aria-label={navigatorLabel ?? copy.workspaceNavigation}
            className={cn(
              'min-h-0 shrink-0 overflow-hidden border-r border-border bg-card/50 sm:block',
              compactPane === 'navigator' ? 'block' : 'hidden',
            )}
            style={{ width: navigatorWidth }}
            data-testid="studio-editor-navigator"
          >
            {navigator}
          </aside>
        ) : null}
        <section
          className={cn(
            'min-h-0 min-w-0 flex-1 overflow-hidden sm:block',
            compactPane === 'workspace' ? 'block' : 'hidden',
            canvasClassName,
          )}
          data-testid="studio-editor-canvas"
        >
          {children}
        </section>
        {inspector ? (
          <aside
            aria-label={inspectorLabel ?? copy.workspaceDetails}
            className={cn(
              'min-h-0 shrink-0 overflow-hidden border-l border-border bg-card/50 sm:block',
              compactPane === 'inspector' ? 'block' : 'hidden',
            )}
            style={{ width: inspectorWidth }}
            data-testid="studio-editor-inspector"
          >
            {inspector}
          </aside>
        ) : null}
      </div>
    </div>
  )
}

interface StudioEditorToolbarProps {
  title: string
  /**
   * The registry `route` this toolbar's h1 is the page heading for.
   *
   * `studio-dashboard-shell.tsx` walks every canonical route and requires
   * a `[data-route-heading="<route>"]` element, and it is also what the
   * shell's route-change focus lands on. An editor surface mounted AS a
   * route must pass this; one mounted inside another page must not, or two
   * elements would claim the same route.
   */
  routeHeading?: string
  subtitle?: string
  testId?: string
  subtitleTestId?: string
  center?: ReactNode
  actions?: ReactNode
  className?: string
}

export function StudioEditorToolbar({
  title,
  routeHeading,
  subtitle,
  testId = 'studio-editor-toolbar',
  subtitleTestId,
  center,
  actions,
  className,
}: StudioEditorToolbarProps) {
  return (
    <header
      className={cn(
        'flex min-h-10 shrink-0 flex-wrap items-center gap-2 border-b border-border bg-background px-2 py-1.5 sm:grid sm:h-10 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:px-3 sm:py-0',
        className,
      )}
      data-testid={testId}
    >
      <div className="flex min-w-0 items-center gap-2">
        <h1
          data-route-heading={routeHeading}
          tabIndex={routeHeading ? -1 : undefined}
          className="shrink-0 text-sm font-semibold text-foreground"
        >
          {title}
        </h1>
        {subtitle ? (
          <span className="truncate text-xs text-muted-foreground" data-testid={subtitleTestId}>
            {subtitle}
          </span>
        ) : null}
      </div>
      <div className="order-2 flex min-w-0 items-center justify-end sm:order-none sm:justify-center">{center}</div>
      <div
        className="order-3 flex w-full min-w-0 flex-wrap items-center justify-start gap-2 sm:order-none sm:w-auto sm:flex-nowrap sm:justify-end sm:overflow-x-auto"
        data-testid="studio-editor-toolbar-actions"
      >
        {actions}
      </div>
    </header>
  )
}
